import type { SubscriptionProductId } from '@fruitful/types';
import { useCallback } from 'react';

import { requestBuilder } from '../../../http/RequestBuilder';
import type { User } from '../../../user/types/User';

interface CreateMemberWithSubscriptionBody {
    email: string;
    stytchId: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    dateOfBirth?: string; // ISO date (YYYY-MM-DDT00:00:00Z)
    referralCode?: string;
    visitorId?: string;
    /** Non-prod only — omit for the default trial_premium schedule. */
    subscriptionProductId?: SubscriptionProductId;
}

interface CreateMemberWithSubscriptionParams extends CreateMemberWithSubscriptionBody {
    onSuccess: (user: User) => void;
    onError: (error: Error) => void;
}

const useMutateMemberWithSubscription = requestBuilder.mutation<
    User,
    CreateMemberWithSubscriptionBody
>({
    endpoint: '/api/user/member-with-subscription',
    method: 'POST',
});

export const useCreateMemberWithSubscription = () => {
    const { mutate, isPending } = useMutateMemberWithSubscription();

    const createMemberWithSubscription = useCallback(
        ({
            email,
            stytchId,
            firstName,
            lastName,
            phoneNumber,
            dateOfBirth,
            referralCode,
            visitorId,
            subscriptionProductId,
            onSuccess,
            onError,
        }: CreateMemberWithSubscriptionParams) => {
            mutate(
                {
                    body: {
                        email,
                        stytchId,
                        firstName,
                        lastName,
                        phoneNumber,
                        dateOfBirth,
                        referralCode,
                        visitorId,
                        ...(subscriptionProductId != null
                            ? { subscriptionProductId }
                            : {}),
                    },
                },
                {
                    onSuccess,
                    onError,
                },
            );
        },
        [],
    );

    return {
        createMemberWithSubscription,
        isCreatingMemberWithSubscription: isPending,
    };
};
